import { useSyncExternalStore } from 'react';
import {
  resetSyncedDocument,
  type AgentActivity,
  type AgentOpenTool,
  type StudioAgentChats,
  type StudioAgentThread,
} from './studio-agent-client';
import type { IntentResult } from './studio-intent';

/**
 * The session's Okra Agent exchanges (W.4.105, streaming since W.4.106). A
 * module store rather than panel state: the Agent panel unmounts whenever
 * another tool is selected, and the conversation — an answer still streaming
 * included — has to be there when it comes back. It lasts as long as the tab
 * and the signed-in account do: sign-out and sign-in are client-side
 * navigations, so `setOwner` resets it when the account changes. Prompts are
 * never written to storage; the history a reload shows comes from the Agent.
 *
 * Since W.4.132 each document has its own chat here, under its document key:
 * the panel shows the chat of the open PDF and nothing else. The Agent keeps
 * one or more chats per document (`<key>.<n>`); the one on screen is `thread`,
 * and New chat starts the next one without deleting the others. The chat
 * under `null` holds what was asked with no PDF open (or with no key for it):
 * it shows on that page only, and goes to the Agent as a chat of its own.
 */

/** Who answers: the Agent in its workspace, or the tool picker (`/api/ai/studio`). */
export type AgentExchangeSource = 'workspace' | 'route';

export interface AgentExchange {
  id: string;
  prompt: string;
  /** The tool picker's answer. `null` for a workspace answer, which is `text`. */
  result: IntentResult | null;
  error: string | null;
  pending: boolean;
  /** The workspace Agent's answer so far. */
  text: string;
  /** The workspace files the Agent looked at, in the order it started on them. */
  activities: AgentActivity[];
  /** Studio tools the Agent proposed. */
  openTools: AgentOpenTool[];
  source: AgentExchangeSource;
  /** What is happening before the answer starts; `null` reads as "Working…". */
  status: string | null;
  /** A lasting line about the document, e.g. that it is too large to read. */
  note: string | null;
  /** The visitor stopped the answer; what had streamed stays. */
  stopped: boolean;
  /**
   * Loaded from the Agent's history rather than asked in this page session.
   * The panel shows these outside its live region: a screen reader announces
   * a new answer, not the stored conversation every time the panel opens.
   */
  fromHistory: boolean;
}

/** One document's chat as the panel shows it. Replaced, never mutated. */
export interface AgentChat {
  /** The chat at the Agent, `<key>.<n>`; null until its list is in or a turn needs one. */
  thread: string | null;
  /** The Agent's chats about the document, the most recent first, as last loaded or begun here. */
  threads: StudioAgentThread[];
  exchanges: AgentExchange[];
}

/** A prompt the home sent along with a file, waiting for the Agent panel to open. */
export interface PendingAgentPrompt {
  prompt: string;
  /** The file it was sent with: the panel drops the prompt if another PDF is open. */
  documentName: string;
}

/**
 * How long a handed-off prompt waits for the Agent panel. The panel opens
 * right after the file does; a prompt older than this was left by a hand-off
 * that never arrived, and must not be sent on whatever PDF is opened later.
 */
const PENDING_PROMPT_TTL_MS = 30_000;

// Snapshots are replaced, never mutated: useSyncExternalStore compares by identity.
const EMPTY: AgentExchange[] = [];
const NO_THREADS: StudioAgentThread[] = [];
// Each document's chat, by document key.
const chats = new Map<string | null, AgentChat>();
let nextId = 0;
// The account the exchanges belong to. `undefined` until the workbench reports one.
let owner: string | null | undefined;
// Counts account changes, so work started for one account cannot land on the next.
let session = 0;
let pendingPrompt: PendingAgentPrompt | null = null;
let pendingSince = 0;
// Text for the Agent's message box, waiting for the panel to open. Never sent from here.
let composerText: string | null = null;
// The documents whose chats were asked for in this account session.
const historyClaimed = new Set<string | null>();
let workspaceDown = false;
// Counts Delete all, so a history asked for before it cannot bring back what it deleted.
let wipes = 0;
// The key of the chat asked with no PDF open; see `noDocumentThread`.
let noDocumentKey: string | null = null;
// How to stop each answer still in flight. Not part of the snapshot.
const aborts = new Map<string, () => void>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function chatOf(key: string | null): AgentChat {
  return chats.get(key) ?? { thread: null, threads: NO_THREADS, exchanges: EMPTY };
}

function commit(key: string | null, next: AgentChat): void {
  chats.set(key, next);
  notify();
}

/**
 * Change one exchange, in whichever chat holds it. An id that is gone
 * (cleared mid-flight) is ignored; with `whilePending`, so is an update for an
 * answer that already ended — a delta or a result that lands after Stop must
 * not reopen it.
 */
function patch(
  id: string,
  change: (exchange: AgentExchange) => Partial<AgentExchange> | null,
  options?: { whilePending: boolean },
): void {
  for (const [key, chat] of chats) {
    const current = chat.exchanges.find((exchange) => exchange.id === id);
    if (!current) continue;
    if (options?.whilePending && !current.pending) return;
    const changed = change(current);
    if (changed === null) return;
    const next = { ...current, ...changed };
    if (!next.pending) aborts.delete(id);
    commit(key, {
      ...chat,
      exchanges: chat.exchanges.map((exchange) => (exchange === current ? next : exchange)),
    });
    return;
  }
}

function stopAll(): void {
  const running = [...aborts.values()];
  aborts.clear();
  for (const abort of running) abort();
}

/** Stop the answers in flight in one chat; what streamed stays, marked stopped. */
function stopChat(key: string | null): void {
  for (const exchange of chatOf(key).exchanges) {
    if (exchange.pending) studioAgentStore.stop(exchange.id);
  }
}

/**
 * The chat a turn with no PDF open goes to: `<key>.1`, where the key is random,
 * made once per page load in the shape of a document key. The Agent keeps it
 * apart from every PDF's chats and from the one conversation (`''`) it held
 * before chats, which has turns about other PDFs; and no document has that
 * key, so the Agent never reads one for it. Its answers show on that page
 * only, and a reload starts another.
 */
function noDocumentThread(): string {
  if (noDocumentKey === null) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    noDocumentKey = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${noDocumentKey}.1`;
}

/** The number of a chat id: `<key>.<n>`. */
function threadNumber(thread: string): number {
  return Number(thread.slice(thread.lastIndexOf('.') + 1)) || 0;
}

/** An answer the Agent stored: the chat it is in exists at the Agent. */
function isStoredAnswer(exchange: AgentExchange): boolean {
  return (
    exchange.source === 'workspace' &&
    !exchange.pending &&
    !exchange.stopped &&
    exchange.error === null
  );
}

/**
 * The list with the chat on screen in it, when the Agent holds that chat and
 * the list, loaded before its first answer, does not say so yet.
 */
function listedWithCurrent(chat: AgentChat): StudioAgentThread[] {
  const { thread } = chat;
  if (thread === null || chat.threads.some((entry) => entry.id === thread)) return chat.threads;
  const first = chat.exchanges.find(isStoredAnswer);
  if (!first) return chat.threads;
  return [{ id: thread, preview: first.prompt, updatedAt: Date.now() }, ...chat.threads];
}

function blank(id: string, prompt: string, source: AgentExchangeSource): AgentExchange {
  return {
    id,
    prompt,
    result: null,
    error: null,
    pending: true,
    text: '',
    activities: [],
    openTools: [],
    source,
    status: null,
    note: null,
    stopped: false,
    fromHistory: false,
  };
}

export const studioAgentStore = {
  /** The exchanges of a document's chat. */
  get(key: string | null = null): AgentExchange[] {
    return chats.get(key)?.exchanges ?? EMPTY;
  },
  /** A document's chat, or null before anything was loaded or asked about it. */
  chat(key: string | null): AgentChat | null {
    return chats.get(key) ?? null;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Record a prompt in a document's chat as in flight and return the id to settle it with. */
  begin(
    prompt: string,
    source: AgentExchangeSource = 'workspace',
    key: string | null = null,
  ): string {
    // Ids keep counting across `clear()`, so a late answer cannot land on a new exchange.
    nextId += 1;
    const id = `exchange-${nextId}`;
    const chat = chatOf(key);
    commit(key, { ...chat, exchanges: [...chat.exchanges, blank(id, prompt, source)] });
    return id;
  },
  /**
   * The chat a turn about `key` goes to: the one on screen, or the first one
   * when the Agent's list never came. With no key (no PDF open), the page's
   * own chat: see `noDocumentThread`.
   */
  threadFor(key: string | null): string {
    const chat = chatOf(key);
    if (chat.thread !== null) return chat.thread;
    const thread = key === null ? noDocumentThread() : `${key}.1`;
    commit(key, { ...chat, thread });
    return thread;
  },
  /**
   * Stop every answer in flight, about every PDF; what streamed stays, marked
   * stopped. Delete all calls it before the Agent deletes anything.
   */
  stopEveryChat(): void {
    for (const key of [...chats.keys()]) stopChat(key);
  },
  /**
   * A turn belongs to its document. Another PDF on screen stops the answers
   * still in flight about any other, so a new upload never replaces the file
   * an answer is reading; what streamed stays, marked stopped.
   */
  stopOtherChats(key: string | null): void {
    for (const other of [...chats.keys()]) {
      if (other !== key) stopChat(other);
    }
  },
  /**
   * Start the next chat about the document. The one on screen stays at the
   * Agent and in the list. Not while an answer is in flight.
   */
  newChat(key: string): void {
    const chat = chatOf(key);
    if (chat.exchanges.some((exchange) => exchange.pending)) return;
    const threads = listedWithCurrent(chat);
    const highest = Math.max(
      0,
      ...threads.map((entry) => threadNumber(entry.id)),
      ...(chat.thread ? [threadNumber(chat.thread)] : []),
    );
    commit(key, { thread: `${key}.${highest + 1}`, threads, exchanges: EMPTY });
  },
  /**
   * Show another of the document's chats. Its messages come with the next
   * `hydrate`. Not while an answer is in flight.
   */
  openChat(key: string, thread: string): void {
    const chat = chatOf(key);
    if (chat.thread === thread || chat.exchanges.some((exchange) => exchange.pending)) return;
    commit(key, { thread, threads: listedWithCurrent(chat), exchanges: EMPTY });
  },
  /** How to stop this answer. Called by `stop`, `clear` and an account change. */
  setAbort(id: string, abort: () => void): void {
    for (const chat of chats.values()) {
      if (chat.exchanges.some((exchange) => exchange.id === id && exchange.pending)) {
        aborts.set(id, abort);
      }
    }
  },
  /** The line shown while the answer has not started (`null` = "Working…"). */
  setStatus(id: string, status: string | null): void {
    patch(id, () => ({ status }), { whilePending: true });
  },
  setNote(id: string, note: string): void {
    patch(id, () => ({ note }));
  },
  appendText(id: string, delta: string): void {
    if (delta === '') return;
    patch(id, (exchange) => ({ text: exchange.text + delta, status: null }), {
      whilePending: true,
    });
  },
  /**
   * A file read began: what was said so far was narration ("Let me read page 1"),
   * and the Agent stores the answer without it, so the panel drops it too.
   */
  resetText(id: string): void {
    patch(id, (exchange) => (exchange.text === '' ? null : { text: '' }), { whilePending: true });
  },
  /** Add an activity, or move the one with the same id to its new state. */
  upsertActivity(id: string, activity: AgentActivity): void {
    patch(
      id,
      (exchange) => ({
        status: null,
        activities: exchange.activities.some((entry) => entry.id === activity.id)
          ? exchange.activities.map((entry) => (entry.id === activity.id ? activity : entry))
          : [...exchange.activities, activity],
      }),
      { whilePending: true },
    );
  },
  /** Add a proposed tool; a tool already proposed in this answer is not added twice. */
  addOpenTool(id: string, openTool: AgentOpenTool): void {
    patch(
      id,
      (exchange) =>
        exchange.openTools.some((entry) => entry.toolId === openTool.toolId)
          ? null
          : { openTools: [...exchange.openTools, openTool] },
      { whilePending: true },
    );
  },
  /** The workspace answer is complete. */
  finish(id: string): void {
    patch(id, () => ({ pending: false, status: null }), { whilePending: true });
  },
  /** The tool picker answered: this exchange is a `route` one, whatever it began as. */
  resolve(id: string, result: IntentResult): void {
    patch(id, () => ({ result, error: null, pending: false, status: null, source: 'route' }), {
      whilePending: true,
    });
  },
  /** End with a line to show. Text that had streamed stays above it. */
  fail(id: string, message: string): void {
    patch(id, () => ({ result: null, error: message, pending: false, status: null }), {
      whilePending: true,
    });
  },
  /** The visitor stopped the answer: abort the request, keep what streamed. */
  stop(id: string): void {
    const abort = aborts.get(id);
    patch(id, () => ({ pending: false, status: null, stopped: true }), { whilePending: true });
    abort?.();
  },
  /**
   * Put the Agent's own history of a document's chat first. Each user message
   * becomes an exchange, answered by the assistant message that follows it.
   * The Agent's history replaces the workspace answers held here, which it
   * already contains; a turn still in flight and the tool picker's answers,
   * which the Agent never saw, stay after it. The list of chats is replaced
   * too. Ignored when the account changed since the history was asked for;
   * the messages are ignored when another chat is on screen by now.
   */
  hydrate(
    key: string | null,
    state: Pick<StudioAgentChats, 'thread' | 'threads' | 'messages'>,
    forSession: number,
  ): void {
    if (forSession !== session) return;
    const chat = chatOf(key);
    const thread = chat.thread ?? state.thread ?? (key === null ? null : `${key}.1`);
    if (thread !== state.thread && state.thread !== null) {
      commit(key, { ...chat, threads: state.threads });
      return;
    }
    const { messages } = state;
    const history: AgentExchange[] = [];
    let open: AgentExchange | null = null;
    for (const message of messages) {
      if (message.role === 'user') {
        open = {
          ...blank(`history-${history.length + 1}`, message.text, 'workspace'),
          pending: false,
          fromHistory: true,
        };
        history.push(open);
      } else if (open) {
        // An answer with no question before it (the window cut it off) is left out.
        open.text = message.text;
        open.openTools = message.openTools;
        open = null;
      }
    }
    const kept = chat.exchanges.filter(
      (exchange) => exchange.pending || exchange.source === 'route',
    );
    const sameThreads =
      state.threads.length === chat.threads.length &&
      state.threads.every((entry, index) => entry.id === chat.threads[index]?.id);
    if (
      history.length === 0 &&
      kept.length === chat.exchanges.length &&
      thread === chat.thread &&
      sameThreads
    ) {
      return;
    }
    commit(key, { thread, threads: state.threads, exchanges: [...history, ...kept] });
  },
  /**
   * Empty the chat on screen: stops its answers in flight and drops a prompt on
   * its way in. The panel calls it once the Agent has deleted that chat (or is
   * not there at all); the document's other chats stay, and so does its PDF in
   * the workspace.
   */
  clear(key: string | null = null): void {
    stopChat(key);
    const chat = chatOf(key);
    if (chat.exchanges.length === 0 && pendingPrompt === null) return;
    pendingPrompt = null;
    commit(key, {
      ...chat,
      threads: chat.threads.filter((entry) => entry.id !== chat.thread),
      exchanges: EMPTY,
    });
  },
  /**
   * Empty every chat, about every PDF and the one with none open. The panel
   * calls it once the Agent has deleted everything it held (`DELETE /state`):
   * every chat and the PDF in its workspace. So the upload memo goes too, and
   * the open PDF is sent again with the next turn. Stops the answers in flight
   * and drops a prompt on its way in; the account session stays, and a history
   * asked for before this is ignored when it lands.
   */
  clearAll(): void {
    stopAll();
    wipes += 1;
    pendingPrompt = null;
    resetSyncedDocument();
    chats.clear();
    notify();
  },
  /** Counts `clearAll`: a history load compares it before and after, as it does `session`. */
  wipes(): number {
    return wipes;
  },
  /**
   * Forget the session: every chat, the prompt on its way in, the upload memo,
   * and which histories were loaded. For a change of account, where the next
   * one must start from its own Agent.
   */
  reset(): void {
    stopAll();
    session += 1;
    pendingPrompt = null;
    composerText = null;
    historyClaimed.clear();
    workspaceDown = false;
    resetSyncedDocument();
    chats.clear();
    notify();
  },
  /**
   * Report the signed-in account (`null` when signed out). The session is
   * reset when it is a different account from the one that wrote it, and on
   * sign-out; the same account coming back keeps its session.
   */
  setOwner(userId: string | null): void {
    if (userId === owner) return;
    const first = owner === undefined;
    owner = userId;
    // The first report only names who is here: no earlier account wrote anything.
    if (!first) studioAgentStore.reset();
  },
  /** The account session a piece of async work belongs to; see `hydrate`. */
  session(): number {
    return session;
  },
  /**
   * Ask to load the Agent's chats about a document. True once per document
   * and account session, so two mounts (or React's double effect) make one
   * request. `releaseHistory` hands the turn back: after a failed load, or to
   * load another of the document's chats.
   */
  claimHistory(key: string | null = null): boolean {
    if (historyClaimed.has(key)) return false;
    historyClaimed.add(key);
    return true;
  },
  releaseHistory(key: string | null = null): void {
    historyClaimed.delete(key);
  },
  /** The workspace Agent answered "unavailable": prompts go to the tool picker until it is back. */
  workspaceDown(): boolean {
    return workspaceDown;
  },
  setWorkspaceDown(down: boolean): void {
    if (down === workspaceDown) return;
    workspaceDown = down;
    // The panel offers Delete all only while the Agent is there.
    notify();
  },
  /** Leave a prompt for the Agent panel, which is about to open on `documentName`. */
  handOff(prompt: string, documentName: string): void {
    pendingPrompt = { prompt, documentName };
    pendingSince = Date.now();
    notify();
  },
  pendingPrompt(): PendingAgentPrompt | null {
    return pendingPrompt;
  },
  /**
   * Take the waiting prompt. It is handed out once, however many effects ask,
   * and not at all once it has waited too long.
   */
  takePendingPrompt(): PendingAgentPrompt | null {
    const taken = pendingPrompt;
    if (taken === null) return null;
    pendingPrompt = null;
    notify();
    return Date.now() - pendingSince > PENDING_PROMPT_TTL_MS ? null : taken;
  },
  /**
   * Put text in the Agent's message box for the visitor to send (W.4.132). The
   * panel may not be mounted yet, so it waits here. Unlike `handOff`, nothing
   * sends it, so it has no time limit.
   */
  fillComposer(text: string): void {
    composerText = text;
    notify();
  },
  composerText(): string | null {
    return composerText;
  },
  /** Take the waiting text. Handed out once, however many effects ask. */
  takeComposerText(): string | null {
    const taken = composerText;
    if (taken === null) return null;
    composerText = null;
    notify();
    return taken;
  },
};

/** The exchanges of a document's chat. */
export function useAgentExchanges(key: string | null = null): AgentExchange[] {
  const read = () => studioAgentStore.get(key);
  return useSyncExternalStore(studioAgentStore.subscribe, read, read);
}

/** A document's chat: which one is on screen, and the document's other chats. */
export function useAgentChat(key: string | null): AgentChat | null {
  const read = () => studioAgentStore.chat(key);
  return useSyncExternalStore(studioAgentStore.subscribe, read, read);
}

/** The prompt waiting for the Agent panel, or `null`. */
export function usePendingAgentPrompt(): PendingAgentPrompt | null {
  return useSyncExternalStore(
    studioAgentStore.subscribe,
    studioAgentStore.pendingPrompt,
    studioAgentStore.pendingPrompt,
  );
}

/** The text waiting for the Agent's message box, or `null`. */
export function useAgentComposerText(): string | null {
  return useSyncExternalStore(
    studioAgentStore.subscribe,
    studioAgentStore.composerText,
    studioAgentStore.composerText,
  );
}

/** Whether the workspace Agent answered that it is not there; see `workspaceDown`. */
export function useAgentWorkspaceDown(): boolean {
  return useSyncExternalStore(
    studioAgentStore.subscribe,
    studioAgentStore.workspaceDown,
    studioAgentStore.workspaceDown,
  );
}

/** Changes when the account does, so a mounted panel loads the next account's history. */
export function useAgentSession(): number {
  return useSyncExternalStore(
    studioAgentStore.subscribe,
    studioAgentStore.session,
    studioAgentStore.session,
  );
}
